/**
 * useNotifications Hook
 * Version: 6.2.0
 * 
 * State management for notifications
 */
import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Notification,
  NotificationCategory,
  NotificationPriority,
  NotificationStatus,
  NotificationFilter,
  NotificationSettings,
  createDefaultSettings,
  createNotification,
  NotificationSummary,
} from '../types/notifications'
import { sampleNotifications } from '../data/sampleNotifications'

// ============================================
// Hook Return Type
// ============================================
interface UseNotificationsReturn {
  // State
  notifications: Notification[]
  unreadCount: number
  settings: NotificationSettings
  isLoading: boolean
  
  // Filtered notifications
  filteredNotifications: Notification[]
  recentNotifications: Notification[]
  highPriorityNotifications: Notification[]
  
  // Summary
  summary: NotificationSummary
  
  // Actions
  addNotification: (notification: Partial<Notification> & { title: string; message: string; category: NotificationCategory }) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  dismissNotification: (id: string) => void
  clearAll: () => void
  archiveNotification: (id: string) => void
  
  // Settings
  updateSettings: (settings: Partial<NotificationSettings>) => void
  resetSettings: () => void
  
  // Filters
  setFilter: (filter: NotificationFilter) => void
  clearFilter: () => void
  
  // Toast queue
  showToast: (notification: Notification) => void
  toastQueue: Notification[]
  dismissToast: (id: string) => void
}

// ============================================
// Hook Implementation
// ============================================
export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>(sampleNotifications)
  const [settings, setSettings] = useState<NotificationSettings>(createDefaultSettings())
  const [filter, setFilter] = useState<NotificationFilter>({})
  const [toastQueue, setToastQueue] = useState<Notification[]>([])
  const [isLoading] = useState(false)

  // Computed: filtered notifications
  const filteredNotifications = useMemo(() => {
    let result = [...notifications]
    
    if (filter.categories?.length) {
      result = result.filter(n => filter.categories!.includes(n.category))
    }
    
    if (filter.priorities?.length) {
      result = result.filter(n => filter.priorities!.includes(n.priority))
    }
    
    if (filter.status) {
      result = result.filter(n => n.status === filter.status)
    }
    
    if (filter.searchQuery) {
      const query = filter.searchQuery.toLowerCase()
      result = result.filter(
        n => 
          n.title.toLowerCase().includes(query) ||
          n.message.toLowerCase().includes(query)
      )
    }
    
    if (filter.dateRange) {
      result = result.filter(
        n => n.timestamp >= filter.dateRange!.start && n.timestamp <= filter.dateRange!.end
      )
    }
    
    // Sort by timestamp (newest first)
    return result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  }, [notifications, filter])

  // Computed: recent notifications (last 5)
  const recentNotifications = useMemo(() => {
    return [...notifications]
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 5)
  }, [notifications])

  // Computed: high priority notifications
  const highPriorityNotifications = useMemo(() => {
    return notifications.filter(
      n => n.priority === NotificationPriority.HIGH || n.priority === NotificationPriority.URGENT
    )
  }, [notifications])

  // Computed: unread count
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.status === NotificationStatus.UNREAD).length
  }, [notifications])

  // Computed: summary
  const summary = useMemo<NotificationSummary>(() => {
    const byCategory = {} as Record<NotificationCategory, number>
    const byPriority = {} as Record<NotificationPriority, number>
    
    Object.values(NotificationCategory).forEach(cat => {
      byCategory[cat] = 0
    })
    Object.values(NotificationPriority).forEach(pri => {
      byPriority[pri] = 0
    })
    
    notifications.forEach(n => {
      byCategory[n.category]++
      byPriority[n.priority]++
    })
    
    return {
      total: notifications.length,
      unread: unreadCount,
      byCategory,
      byPriority,
      recent: recentNotifications,
    }
  }, [notifications, unreadCount, recentNotifications])

  // Actions
  const addNotification = useCallback((notification: Partial<Notification> & { title: string; message: string; category: NotificationCategory }) => {
    const newNotification = createNotification(notification)
    setNotifications(prev => [newNotification, ...prev])
    
    // Auto show toast for high priority
    if (
      newNotification.priority === NotificationPriority.HIGH ||
      newNotification.priority === NotificationPriority.URGENT
    ) {
      setToastQueue(prev => [...prev, newNotification])
    }
  }, [])

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === id ? { ...n, status: NotificationStatus.READ, readAt: new Date() } : n
      )
    )
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({
        ...n,
        status: NotificationStatus.READ as NotificationStatus,
        readAt: new Date(),
      }))
    )
  }, [])

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const archiveNotification = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === id ? { ...n, status: NotificationStatus.ARCHIVED } : n
      )
    )
  }, [])

  const clearAll = useCallback(() => {
    setNotifications([])
  }, [])

  const updateSettings = useCallback((newSettings: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }, [])

  const resetSettings = useCallback(() => {
    setSettings(createDefaultSettings())
  }, [])

  const clearFilter = useCallback(() => {
    setFilter({})
  }, [])

  // Toast management
  const showToast = useCallback((notification: Notification) => {
    setToastQueue(prev => [...prev, notification])
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToastQueue(prev => prev.filter(n => n.id !== id))
  }, [])

  // Auto-dismiss toasts after 5 seconds
  useEffect(() => {
    if (toastQueue.length === 0) return
    
    const timer = setTimeout(() => {
      setToastQueue(prev => prev.slice(1))
    }, 5000)
    
    return () => clearTimeout(timer)
  }, [toastQueue])

  return {
    // State
    notifications,
    unreadCount,
    settings,
    isLoading,
    
    // Filtered notifications
    filteredNotifications,
    recentNotifications,
    highPriorityNotifications,
    
    // Summary
    summary,
    
    // Actions
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    archiveNotification,
    
    // Settings
    updateSettings,
    resetSettings,
    
    // Filters
    setFilter,
    clearFilter,
    
    // Toast queue
    showToast,
    toastQueue,
    dismissToast,
  }
}

// ============================================
// Export
// ============================================
export type { UseNotificationsReturn }